/**
 * Shared Safari/WebKit browser detection.
 *
 * Safari (and all iOS browsers, which use WebKit under the hood) has issues
 * with clip-path and SVG mask animations used in page transitions. This module
 * provides a single source of truth for detection so we can apply fallbacks.
 *
 * Uses a data attribute (data-safari) on <html> for CSS selectors (set by an
 * inline <head> script), and a useState/useEffect hook for React components
 * to avoid hydration mismatches.
 */

import { useState, useEffect } from 'react'

/**
 * Inline script to run in <head> before first paint.
 * Sets data-safari attribute on <html> so CSS can branch on it.
 */
export const safariDetectionScript = `(function(){var u=navigator.userAgent.toLowerCase();if((u.includes('safari')&&!u.includes('chrome')&&!u.includes('chromium')&&!u.includes('android')&&!u.includes('firefox'))||u.includes('crios')){document.documentElement.setAttribute('data-safari','')}})()`

/**
 * Detects Safari/WebKit from the user agent string.
 */
function detectSafari(): boolean {
	if (typeof navigator === 'undefined') return false
	const ua = navigator.userAgent.toLowerCase()
	const isSafari =
		ua.includes('safari') &&
		!ua.includes('chrome') &&
		!ua.includes('chromium') &&
		!ua.includes('android') &&
		!ua.includes('firefox')
	const isChromeOniOS = ua.includes('crios')
	return isSafari || isChromeOniOS
}

/**
 * Hook that detects Safari/WebKit browsers.
 * Returns false during SSR and initial hydration to avoid mismatches,
 * then updates to the real value after mount.
 */
export function useIsSafari(): boolean {
	const [isSafari, setIsSafari] = useState(false)
	useEffect(() => {
		setIsSafari(detectSafari())
	}, [])
	return isSafari
}
